// Confirmación de "Pedir a <proveedor>" desde la lista de compra: las líneas
// que irán en la orden, la fecha de entrega (propuesta según el proveedor y
// siempre editable) y el total estimado. La orden se crea SIN enviar
// (enviarla por email es otra acción, en Compras y Almacén). Al crearla enseña
// su número y el enlace a Compras y Almacén.
//
// Proveedor por día (`entrega.pedido_por_dia`): cada línea enseña una fila por
// día con la fecha, los paquetes editables (entero, mínimo 0), la cantidad
// resultante y la fecha de entrega de ese día (editable); el total se
// recalcula al editar y se envían las `entregas` (los días a 0 no van).
// Compra directa (`entrega.modo_suministro === 'compra_directa'`): "Lista para
// comprar en <proveedor>", sin nada que enviar.
//
// Props: abierto, grupo ({titulo, lineas}), fechaInicial (AAAA-MM-DD),
// entrega ({modo_suministro, pedido_por_dia, dias_entrega, aviso, ...} del
// proveedor, de `totales_proveedor`), creando, resultado ({orden_id,
// orden_numero, message, avisos}), onConfirmar(fecha, entregas), onCerrar.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatEuros, conUnidad, textoPaquetesCorto, textoDia, avisoDiaSinEntrega,
} from './listaCompraUtils';

const claseBoton = 'h-[34px] px-3 rounded-lg border text-[13px] font-bold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const claseInput = 'h-[30px] px-2 text-[13px] border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary';

const clave = (l, d) => `${l.ingrediente_id}|${d.fecha || ''}`;
const conPaquete = (l) => l.paquetes !== null && l.paquetes !== undefined;

// Coste de n paquetes (o n unidades de receta sin paquete) de una línea
const costeDe = (l, n) => {
  const dias = l.por_dia || [];
  const conPrecio = dias.find(d => Number(d.paquetes) > 0 && Number(d.coste_estimado) > 0);
  if (conPaquete(l)) {
    if (conPrecio) return n * (Number(conPrecio.coste_estimado) / Number(conPrecio.paquetes));
    const cpuc = Number(l.cantidad_por_unidad_compra) || 1;
    if (Number(l.precio_paquete) > 0 && cpuc !== 1) return n * Number(l.precio_paquete);
    return n * cpuc * (Number(l.precio_unitario) || 0);
  }
  return n * (Number(l.precio_unitario) || 0);
};

// Valor calculado de un día: los paquetes (o la cantidad, sin paquete) y la fecha propuesta
const valorCalculado = (l, d) => ({
  cantidad: conPaquete(l) ? Number(d.paquetes) || 0 : Number(d.cantidad_compra) || 0,
  fechaEntrega: d.fecha_entrega || '',
});

const diasIniciales = (grupo) => {
  const out = {};
  (grupo?.lineas || []).forEach((l) => {
    (l.por_dia || []).forEach((d) => {
      out[clave(l, d)] = valorCalculado(l, d);
    });
  });
  return out;
};

const ConfirmarPedido = ({
  abierto, grupo, fechaInicial = '', entrega = null, creando = false, resultado = null, onConfirmar, onCerrar,
}) => {
  const [fecha, setFecha] = useState(fechaInicial || '');
  const [dias, setDias] = useState(() => diasIniciales(grupo)); // {ingrediente|fecha: {cantidad, fechaEntrega}}

  useEffect(() => {
    if (abierto) {
      setFecha(fechaInicial || '');
      setDias(diasIniciales(grupo));
    }
  }, [abierto, fechaInicial, grupo]);

  const lineas = useMemo(() => (grupo?.lineas || []).filter(l => l.pedible), [grupo]);
  const porDia = !!entrega?.pedido_por_dia && lineas.some(l => (l.por_dia || []).length > 0);
  const compraDirecta = entrega?.modo_suministro === 'compra_directa';
  const diasEntrega = entrega?.dias_entrega || [];

  const valorDia = (l, d) => dias[clave(l, d)] || valorCalculado(l, d);
  const cambiarDia = (l, d, cambios) => setDias(prev => ({ ...prev, [clave(l, d)]: { ...valorDia(l, d), ...cambios } }));

  const costeLinea = (l) => {
    if (!porDia || !(l.por_dia || []).length) return Number(l.coste_estimado) || 0;
    return l.por_dia.reduce((s, d) => s + costeDe(l, Number(valorDia(l, d).cantidad) || 0), 0);
  };
  const cantidadLinea = (l) => (l.por_dia || []).reduce((s, d) => s + (Number(valorDia(l, d).cantidad) || 0), 0);

  if (!abierto || !grupo) return null;
  const total = lineas.reduce((s, l) => s + costeLinea(l), 0);
  const sinPrecio = lineas.filter(l => l.sin_precio).length;
  const algoQuePedir = !porDia || lineas.some(l => !(l.por_dia || []).length || cantidadLinea(l) > 0);
  const avisoFecha = !porDia ? avisoDiaSinEntrega(fecha, diasEntrega, grupo.titulo) : '';

  const confirmar = () => {
    if (!porDia) {
      onConfirmar(fecha || null, null);
      return;
    }
    const entregas = lineas.filter(l => (l.por_dia || []).length > 0).map(l => ({
      ingrediente_id: l.ingrediente_id,
      dias: l.por_dia
        .map(d => ({ d, v: valorDia(l, d) }))
        .filter(({ v }) => Number(v.cantidad) > 0)
        .map(({ d, v }) => ({ fecha: d.fecha, cantidad_envases: Number(v.cantidad), fecha_entrega: v.fechaEntrega || null })),
    }));
    onConfirmar(null, entregas);
  };

  const titulo = compraDirecta ? `Lista para comprar en ${grupo.titulo}` : `Pedir a ${grupo.titulo}`;
  const nLineas = `${lineas.length} ${lineas.length === 1 ? 'línea' : 'líneas'}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" data-testid="confirmar-pedido">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-[#E1E6E3]">
          <h2 className="text-lg font-extrabold text-[#16221D]">{titulo}</h2>
          <p className="text-xs text-[#5B6B64]">
            {compraDirecta
              ? 'Se crea una lista de compra para ir a comprar: no se envía nada al proveedor.'
              : 'La orden se crea sin enviar: la revisas y la envías desde Compras y Almacén.'}
          </p>
          {porDia && (
            <p className="mt-1 text-xs font-semibold text-[#0F5E43]" data-testid="confirmar-pedido-por-dia">
              Este proveedor sirve por día: ajusta los paquetes de cada entrega
            </p>
          )}
        </div>

        {resultado ? (
          <div className="px-5 py-4 flex flex-col gap-3 text-sm" data-testid="confirmar-pedido-resultado">
            <p className="font-bold text-[#0F5E43]">
              {compraDirecta ? `Lista de compra ${resultado.orden_numero} creada.` : `Orden ${resultado.orden_numero} creada (sin enviar).`}
            </p>
            {(resultado.avisos || []).map(a => <p key={a} className="text-[12px] text-[#92400E]">{a}</p>)}
            <Link
              to={`/compras/necesidades?orden=${encodeURIComponent(resultado.orden_id)}`}
              className="text-[#11704F] font-semibold hover:underline"
              data-testid="confirmar-pedido-enlace"
            >
              {compraDirecta ? 'Ver la lista en Compras y Almacén' : 'Ver la orden en Compras y Almacén'}
            </Link>
            <div className="flex justify-end">
              <button type="button" onClick={onCerrar} className={`${claseBoton} bg-duetto-primary border-duetto-primary text-white`}>Cerrar</button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-4 flex flex-col gap-3">
            <ul className="divide-y divide-[#EEF1EF] text-[13px]">
              {lineas.map(l => (
                <li key={l.clave} className="py-1">
                  <div className="flex items-start justify-between gap-3">
                    <span className="min-w-0">
                      <span className="font-semibold text-[#16221D]">{l.nombre}</span>
                      {!(porDia && (l.por_dia || []).length) && (
                        <span className="block text-[11px] text-[#5B6B64]">{textoPaquetesCorto(l) || conUnidad(l.falta_comprar, l.unidad)}</span>
                      )}
                    </span>
                    <span className="tabular-nums whitespace-nowrap">{l.sin_precio ? <span className="text-[#9AA6A0]">sin precio</span> : formatEuros(costeLinea(l))}</span>
                  </div>
                  {porDia && (l.por_dia || []).length > 0 && (
                    <div className="mt-1 flex flex-col gap-1" data-testid={`confirmar-pedido-dias-${l.ingrediente_id}`}>
                      {l.por_dia.map((d) => {
                        const v = valorDia(l, d);
                        const n = Number(v.cantidad) || 0;
                        const cpuc = conPaquete(l) ? (Number(l.cantidad_por_unidad_compra) || 1) : 1;
                        const aviso = avisoDiaSinEntrega(v.fechaEntrega, diasEntrega, grupo.titulo);
                        return (
                          <div key={clave(l, d)} className="flex flex-wrap items-center gap-2 pl-2 text-[12px] text-[#4A5A53]">
                            <span className="w-[72px] font-semibold text-[#16221D]">{textoDia(d.fecha)}</span>
                            <label className="flex items-center gap-1">
                              <input
                                type="number"
                                min="0"
                                step={conPaquete(l) ? 1 : 0.01}
                                value={v.cantidad}
                                onChange={(e) => {
                                  const bruto = Math.max(0, Number(e.target.value) || 0);
                                  cambiarDia(l, d, { cantidad: conPaquete(l) ? Math.round(bruto) : bruto });
                                }}
                                className={`${claseInput} w-[72px] text-right`}
                                aria-label={`${conPaquete(l) ? 'Paquetes' : 'Cantidad'} de ${l.nombre} el ${textoDia(d.fecha)}`}
                                data-testid={`confirmar-pedido-dia-${l.ingrediente_id}-${d.fecha}`}
                              />
                              {conPaquete(l) ? (cpuc !== 1 ? `paq. de ${cpuc} ${l.unidad || ''}` : (l.unidad || '')) : (l.unidad || '')}
                            </label>
                            <span className="tabular-nums">= {conUnidad(n * cpuc, l.unidad)}</span>
                            <label className="flex items-center gap-1">
                              {compraDirecta ? 'compra' : 'entrega'}
                              <input
                                type="date"
                                value={v.fechaEntrega || ''}
                                onChange={(e) => cambiarDia(l, d, { fechaEntrega: e.target.value })}
                                className={claseInput}
                                aria-label={`Fecha de ${compraDirecta ? 'compra' : 'entrega'} de ${l.nombre} para el ${textoDia(d.fecha)}`}
                              />
                            </label>
                            {aviso && <span className="basis-full text-[11px] text-[#92400E]">{aviso}</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            {entrega?.aviso && <p className="text-[12px] text-[#92400E]" data-testid="confirmar-pedido-aviso">{entrega.aviso}</p>}
            <div className="flex flex-wrap items-end justify-between gap-3">
              {!porDia ? (
                <label className="flex flex-col gap-1 text-xs font-semibold text-[#4A5A53]">
                  {compraDirecta ? 'Fecha de compra' : 'Fecha de entrega'}
                  <input
                    type="date"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    className="h-[34px] px-2 text-sm border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    data-testid="confirmar-pedido-fecha"
                  />
                  {avisoFecha && <span className="font-normal text-[11px] text-[#92400E]">{avisoFecha}</span>}
                </label>
              ) : <span />}
              <div className="text-right">
                <div className="text-sm font-extrabold text-[#16221D]">Total estimado: {formatEuros(total)} <span className="font-normal text-[#5B6B64]">sin IVA</span></div>
                {sinPrecio > 0 && <div className="text-[11px] text-[#9AA6A0]">{sinPrecio} sin precio</div>}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onCerrar} disabled={creando} className={`${claseBoton} bg-white border-[#CBD3CF] text-[#16221D] hover:bg-gray-50`}>Cancelar</button>
              <button
                type="button"
                onClick={confirmar}
                disabled={creando || lineas.length === 0 || !algoQuePedir}
                className={`${claseBoton} bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90`}
                data-testid="confirmar-pedido-crear"
              >
                {creando ? 'Creando...' : (compraDirecta ? `Crear lista de compra (${nLineas})` : `Crear orden (${nLineas})`)}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmarPedido;
